import { useState } from 'react';

import { compactUsd, tone } from '../components/ui.tsx';
import { usePoll } from '../hooks/usePoll.ts';
import { fmtUsd, shortAddr } from '../lib/format.ts';
import { relayer, type LeaderPeriod, type LeaderRow } from '../lib/relayer.ts';
import { useApp } from '../state.tsx';

const PERIODS: { id: LeaderPeriod; label: string }[] = [
  { id: '24h', label: '24H' },
  { id: '7d', label: '7D' },
  { id: '30d', label: '30D' },
  { id: 'all', label: 'All Time' },
];
const REFRESH_MS = 30_000;

const signedUsd = (v: bigint) => `${v < 0n ? '-' : '+'}$${fmtUsd(v < 0n ? -v : v, 2)}`;

/** Traders ranked by realized PnL net of fees over a period, the connected one highlighted (and pinned when outside the top) */
export function LeaderboardPage() {
  const { wallet, openConnect } = useApp();
  const [period, setPeriod] = useState<LeaderPeriod>('24h');
  const me = wallet?.address;
  const board = usePoll(() => relayer.leaderboard(period, me), REFRESH_MS, [period, me]);
  const rows = board.data?.rows;
  const mine = board.data?.me;

  return (
    <div className="leaderboard">
      <div className="lb-head">
        <div>
          <h1>Leaderboard</h1>
          <p className="desc">Realized PnL net of fees, closed positions only. Liquidations count as the margin lost.</p>
        </div>
        <div className="seg seg-sm" role="tablist" aria-label="Period">
          {PERIODS.map((p) => (
            <button key={p.id} role="tab" aria-selected={period === p.id} className={period === p.id ? 'is-on' : ''} onClick={() => setPeriod(p.id)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {mine ? (
        <section className="panel lb-me">
          <span className="lb-rank">#{mine.rank}</span>
          <span>Your rank</span>
          <b className={`num ${tone(mine.pnl)}`}>{signedUsd(mine.pnl)}</b>
          <span className="muted">
            {mine.trades} trades · {winRate(mine)} wins
          </span>
        </section>
      ) : !wallet ? (
        <section className="panel lb-me">
          <span>Connect a wallet to see your rank</span>
          <button className="btn btn-primary btn-sm" onClick={() => openConnect(true)}>
            Connect Wallet
          </button>
        </section>
      ) : null}

      <section className="panel">
        {board.error && !rows ? (
          <div className="empty">{`Leaderboard unavailable (${board.error})`}</div>
        ) : !rows ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No closed positions in this period yet: the first spot is free.</div>
        ) : (
          <div className="table-wrap">
            <table className="grid-table lb-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Trader</th>
                  <th className="r">PnL</th>
                  <th className="r">Volume</th>
                  <th className="r">Trades</th>
                  <th className="r">Win Rate</th>
                  <th className="r">Best Trade</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user} className={r.user === me ? 'lb-mine' : ''}>
                    <td>
                      <span className={`lb-rank ${r.rank <= 3 ? `lb-top lb-top-${r.rank}` : ''}`}>{r.rank}</span>
                    </td>
                    <td className="num" title={r.user}>
                      {shortAddr(r.user)}
                      {r.user === me && <span className="tag lb-you">You</span>}
                    </td>
                    <td className={`r num strong ${tone(r.pnl)}`}>{signedUsd(r.pnl)}</td>
                    <td className="r num">{compactUsd(r.volume)}</td>
                    <td className="r num">{r.trades}</td>
                    <td className="r num">{winRate(r)}</td>
                    <td className={`r num ${tone(r.best)}`}>{signedUsd(r.best)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function winRate(r: LeaderRow): string {
  return r.trades === 0 ? '- -' : `${Math.round((r.wins / r.trades) * 100)}%`;
}
